import React, { useMemo, useState } from "react";
import {
  Box,
  Grid,
  Typography,
  LinearProgress,
  Tooltip,
  Stack,
  ToggleButtonGroup,
  ToggleButton,
  IconButton,
  Skeleton,
  Button,
} from "@mui/material";
import { IoReload } from "react-icons/io5";
import { FaPlus } from "react-icons/fa";

import useDashboardData from "../hooks/useDashboardData";
import {
  calculateInventoryMetrics,
  calculateDataHealth,
  calculateIntegrityIssues,
  calculatePortfolioDistribution,
  calculateBrokerMetrics,
  calculateClosings,
  calculateActivityFeed,
  calculateBirthdays,
} from "../utils/dashboardMetrics";

import DashboardKpiCard from "../Components/dashboard/DashboardKpiCard";
import DashboardSection from "../Components/dashboard/DashboardSection";
import PortfolioHealth from "../Components/dashboard/PortfolioHealth";
import PortfolioDistribution from "../Components/dashboard/PortfolioDistribution";
import ClosingsSection from "../Components/dashboard/ClosingsSection";
import ActivityFeed from "../Components/dashboard/ActivityFeed";
import BrokerPerformance from "../Components/dashboard/BrokerPerformance";
import AttentionModal from "../Components/dashboard/AttentionModal";
import BirthdayCard from "../Components/dashboard/BirthdayCard";
import TotalImoveisHistoryDialog from "../Components/dashboard/TotalImoveisHistoryDialog";
import { PremiumCard, dashboardTokens } from "../Components/dashboard/dashboardStyles";
import { ref, get } from "firebase/database";
import { db } from "../../firebaseConfig";

const readCssVar = (name, fallback) => {
  try {
    const v = getComputedStyle(document.documentElement).getPropertyValue(name);
    return v?.trim() || fallback;
  } catch {
    return fallback;
  }
};

const PERIOD_LABELS = { WEEK: "esta semana", MONTH: "este mês", QTD90: "90 dias" };

function Dashboard() {
  const verde = readCssVar("--verde", "#303926");
  const bege = readCssVar("--bege", "#8b7e76");
  const cinza = readCssVar("--cinza", "#1e1e1e");

  const { loading, imoveis, imoveisPublicos, corretores, activityLogs, lastLoadedAt, reload } =
    useDashboardData();

  const [period, setPeriod] = useState("WEEK");
  const [attentionOpen, setAttentionOpen] = useState(false);
  const [attentionFilter, setAttentionFilter] = useState("todos");

  const [totalHistoryOpen, setTotalHistoryOpen] = useState(false);
  const [totalHistory, setTotalHistory] = useState([]);
  const [totalHistoryLoading, setTotalHistoryLoading] = useState(false);

  const inventory = useMemo(() => calculateInventoryMetrics(imoveis), [imoveis]);
  const health = useMemo(() => calculateDataHealth(imoveis), [imoveis]);
  const integrity = useMemo(
    () => calculateIntegrityIssues(imoveis, imoveisPublicos),
    [imoveis, imoveisPublicos]
  );
  const distribution = useMemo(() => calculatePortfolioDistribution(imoveis), [imoveis]);
  const brokers = useMemo(() => calculateBrokerMetrics(imoveis, corretores), [imoveis, corretores]);
  const closings = useMemo(() => calculateClosings(imoveis, period), [imoveis, period]);
  const activity = useMemo(() => calculateActivityFeed(activityLogs), [activityLogs]);
  const birthdays = useMemo(() => calculateBirthdays(imoveis), [imoveis]);

  const totalBrokerCount = Object.keys(corretores || {}).length;
  const activeBrokerCount = brokers.filter((b) => b.ativos > 0).length;
  const attentionCount = health.totalComProblema + integrity.totalIssues;

  const openAttention = (filterKey) => {
    setAttentionFilter(filterKey);
    setAttentionOpen(true);
  };

  const handleOpenTotalHistory = async () => {
    setTotalHistoryOpen(true);
    setTotalHistoryLoading(true);
    try {
      const snap = await get(ref(db, "auditoria/totalImoveisHistorico"));
      if (snap.exists()) {
        const data = snap.val();
        const historico = Object.entries(data)
          .map(([id, value]) => ({ id, ...value }))
          .sort((a, b) => (b.timestamp || 0) - (a.timestamp || 0))
          .slice(0, 50);
        setTotalHistory(historico);
      } else {
        setTotalHistory([]);
      }
    } catch (e) {
      console.error("Erro ao carregar histórico do total de imóveis:", e);
      setTotalHistory([]);
    } finally {
      setTotalHistoryLoading(false);
    }
  };

  const lastLoadedLabel = lastLoadedAt
    ? lastLoadedAt.toLocaleTimeString("pt-BR", { hour: "2-digit", minute: "2-digit" })
    : null;

  return (
    <Box sx={{ ml: { lg: "15rem", md: "13rem", sm: 0 }, p: { xs: 2, md: 3 }, overflowX: "hidden" }}>
      {/* CABEÇALHO */}
      <Stack
        direction={{ xs: "column", md: "row" }}
        alignItems={{ xs: "flex-start", md: "center" }}
        spacing={1.5}
        mb={3}
      >
        <Box>
          <Typography sx={{ fontSize: { xs: "1.3rem", md: "1.5rem" }, fontWeight: 600, color: cinza }}>
            Visão geral
          </Typography>
          <Typography sx={{ fontSize: "0.85rem", color: dashboardTokens.textSecondary, mt: 0.25 }}>
            Acompanhe o portfólio e a operação da Senna Viannini.
          </Typography>
        </Box>

        <Box flexGrow={1} />

        <Stack
          direction="row"
          alignItems="center"
          spacing={1}
          sx={{ width: { xs: "100%", md: "auto" }, justifyContent: { xs: "space-between", md: "flex-end" } }}
        >
          <ToggleButtonGroup value={period} exclusive onChange={(_, v) => v && setPeriod(v)} size="small">
            <ToggleButton value="WEEK" sx={{ textTransform: "none", fontSize: "0.78rem" }}>7 dias</ToggleButton>
            <ToggleButton value="MONTH" sx={{ textTransform: "none", fontSize: "0.78rem" }}>30 dias</ToggleButton>
            <ToggleButton value="QTD90" sx={{ textTransform: "none", fontSize: "0.78rem" }}>90 dias</ToggleButton>
          </ToggleButtonGroup>
          <Stack direction="row" alignItems="center" spacing={0.75}>
            {lastLoadedLabel && (
              <Typography sx={{ fontSize: "0.72rem", color: dashboardTokens.textSecondary }}>
                Atualizado {lastLoadedLabel}
              </Typography>
            )}
            <Tooltip title="Recarregar">
              <IconButton onClick={reload} size="small">
                <IoReload />
              </IconButton>
            </Tooltip>
          </Stack>
        </Stack>
      </Stack>

      {loading && <LinearProgress sx={{ mb: 2 }} />}

      {/* KPIs EXECUTIVOS */}
      {loading ? (
        <Grid container spacing={2} sx={{ mb: 3.5 }}>
          {[1, 2, 3, 4].map((k) => (
            <Grid item xs={12} sm={6} md={3} key={k}>
              <Skeleton variant="rounded" height={112} />
            </Grid>
          ))}
        </Grid>
      ) : (
        <Grid container spacing={2} sx={{ mb: 3.5 }}>
          <Grid item xs={12} sm={6} md={3}>
            <DashboardKpiCard
              label="Imóveis ativos"
              value={inventory.activeCount}
              sublabel={`${inventory.byModalidadeActive.Venda} venda · ${inventory.byModalidadeActive.Aluguel} aluguel`}
              hint={
                <Button
                  onClick={handleOpenTotalHistory}
                  size="small"
                  sx={{ p: 0, minWidth: 0, textTransform: "none", fontSize: "0.74rem", color: dashboardTokens.textSecondary, textDecoration: "underline" }}
                >
                  {inventory.total} no total · ver histórico
                </Button>
              }
              cinza={cinza}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <DashboardKpiCard
              label="Precisam de atenção"
              value={attentionCount}
              sublabel={attentionCount > 0 ? "clique para ver a lista" : "tudo em ordem"}
              onClick={attentionCount > 0 ? () => openAttention("todos") : undefined}
              cinza={cinza}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <DashboardKpiCard
              label="Corretores ativos"
              value={activeBrokerCount}
              sublabel={`de ${totalBrokerCount} cadastrados`}
              cinza={cinza}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <DashboardKpiCard
              label={`Fechamentos — ${PERIOD_LABELS[period]}`}
              value={closings.total}
              sublabel={`${closings.vendas} venda · ${closings.alugueis} aluguel`}
              hint={
                closings.variacaoPct !== null
                  ? `${closings.variacaoPct >= 0 ? "+" : ""}${closings.variacaoPct.toFixed(1)}% vs período anterior`
                  : undefined
              }
              cinza={cinza}
            />
          </Grid>
        </Grid>
      )}

      {/* SAÚDE DO ESTOQUE */}
      <DashboardSection title="Saúde do portfólio" subtitle="Problemas detectados automaticamente em imóveis ativos">
        {loading ? <Skeleton variant="rounded" height={90} /> : (
          <PortfolioHealth health={health} integrity={integrity} onOpenAttention={openAttention} />
        )}
      </DashboardSection>

      {/* PORTFÓLIO */}
      <DashboardSection title="Portfólio" subtitle="Composição do estoque ativo hoje">
        {loading ? <Skeleton variant="rounded" height={220} /> : (
          <PortfolioDistribution distribution={distribution} verde={verde} cinza={cinza} bege={bege} />
        )}
      </DashboardSection>

      {/* FECHAMENTOS */}
      <DashboardSection title="Fechamentos" subtitle="Imóveis marcados como vendidos/alugados pela Senna">
        {loading ? <Skeleton variant="rounded" height={220} /> : (
          <ClosingsSection closings={closings} verde={verde} cinza={cinza} periodLabel={PERIOD_LABELS[period]} />
        )}
      </DashboardSection>

      {/* ATIVIDADE + CORRETORES */}
      <Grid container spacing={2} sx={{ mb: 0.5 }}>
        <Grid item xs={12} lg={5}>
          <DashboardSection title="Atividade recente" subtitle="Últimas edições registradas">
            {loading ? <Skeleton variant="rounded" height={280} /> : <ActivityFeed entries={activity} />}
          </DashboardSection>
        </Grid>
        <Grid item xs={12} lg={7}>
          <DashboardSection title="Corretores" subtitle="Carteira ativa e fechamentos">
            {loading ? <Skeleton variant="rounded" height={280} /> : <BrokerPerformance brokers={brokers} />}
          </DashboardSection>
        </Grid>
      </Grid>

      {/* ANIVERSARIANTES + AÇÕES RÁPIDAS */}
      <Grid container spacing={2}>
        <Grid item xs={12} md={7}>
          {!loading && (
            <BirthdayCard aniversariantes={birthdays} bege={bege} cinza={cinza} />
          )}
        </Grid>
        <Grid item xs={12} md={5}>
          <PremiumCard sx={{ p: 2 }}>
            <Typography sx={{ fontSize: "0.82rem", fontWeight: 600, mb: 1.25 }}>Ações rápidas</Typography>
            <Stack spacing={1}>
              <Button href="/#/panel/AddImovel" startIcon={<FaPlus size={12} />} sx={{ justifyContent: "flex-start", textTransform: "none", color: cinza }}>
                Adicionar imóvel
              </Button>
              <Button href="/#/panel/lista-imoveis" sx={{ justifyContent: "flex-start", textTransform: "none", color: cinza }}>
                Ver todos os imóveis
              </Button>
              <Button href="/#/panel/cadastrar-corretor" sx={{ justifyContent: "flex-start", textTransform: "none", color: cinza }}>
                Ver cadastros de corretores
              </Button>
            </Stack>
          </PremiumCard>
        </Grid>
      </Grid>

      <AttentionModal
        open={attentionOpen}
        onClose={() => setAttentionOpen(false)}
        initialFilter={attentionFilter}
        health={health}
        integrity={integrity}
        corretoresMap={corretores}
      />

      <TotalImoveisHistoryDialog
        open={totalHistoryOpen}
        onClose={() => setTotalHistoryOpen(false)}
        historico={totalHistory}
        loading={totalHistoryLoading}
        verde={verde}
        bege={bege}
        cinza={cinza}
      />
    </Box>
  );
}

export default Dashboard;
